import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Search, Package, Clock, CheckCircle, Truck, ChefHat,
  XCircle, RefreshCw, Copy, Check, MapPin, Receipt,
} from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../../utils/config";

const POLL_INTERVAL = 30_000; // 30 s

const STATUS_STEPS = [
  { key: "pending",    icon: Clock,        label: "Order Placed", color: "text-yellow-500", bg: "bg-yellow-100" },
  { key: "confirmed",  icon: CheckCircle,  label: "Confirmed",    color: "text-blue-500",   bg: "bg-blue-100"   },
  { key: "preparing",  icon: ChefHat,      label: "Preparing",    color: "text-purple-500", bg: "bg-purple-100" },
  { key: "on_the_way", icon: Truck,        label: "On the Way",   color: "text-orange-500", bg: "bg-orange-100" },
  { key: "delivered",  icon: CheckCircle,  label: "Delivered",    color: "text-green-500",  bg: "bg-green-100"  },
];

const STEP_INDEX = Object.fromEntries(STATUS_STEPS.map((s, i) => [s.key, i]));
const statusLabel = (s) => STATUS_STEPS.find((x) => x.key === s)?.label || s;

const TERMINAL = new Set(["delivered", "cancelled"]);

const OrderTracking = () => {
  const { orderId: paramId } = useParams();
  const navigate = useNavigate();

  const [query,   setQuery]   = useState(paramId || "");
  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [copied,  setCopied]  = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const timerRef = useRef(null);

  // ── fetch ───────────────────────────────────────────────────────────────────
  const fetchOrder = useCallback(async (id, silent = false) => {
    if (!id) return;
    if (!silent) setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${apiUrl}/bill/${String(id).trim()}`);
      if (res.data?.status && res.data?.item) {
        setOrder(res.data.item);
        setLastRefresh(new Date());
      } else {
        setOrder(null);
        if (!silent) setError("Order not found. Please check the order number.");
      }
    } catch {
      if (!silent) setError("Unable to fetch order. Please try again.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // ── auto-load when param present ────────────────────────────────────────────
  useEffect(() => {
    if (paramId) {
      setQuery(paramId);
      fetchOrder(paramId);
    }
  }, [paramId, fetchOrder]);

  // ── live polling ────────────────────────────────────────────────────────────
  useEffect(() => {
    clearInterval(timerRef.current);
    if (!order || TERMINAL.has(order.status)) return;
    timerRef.current = setInterval(() => fetchOrder(order.id, true), POLL_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [order, fetchOrder]);

  // ── manual search ────────────────────────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    navigate(`/track/${query.trim()}`);
    fetchOrder(query.trim());
  };

  // ── helpers ──────────────────────────────────────────────────────────────────
  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/track/${order.id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const currentStep  = order ? (STEP_INDEX[order.status] ?? -1) : -1;
  const isCancelled  = order?.status === "cancelled";
  const isLive       = order && !TERMINAL.has(order.status);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto space-y-8">

        {/* Header */}
        <div className="text-center">
          <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Package size={28} className="text-orange-500" />
          </div>
          <h1 className="text-3xl font-extrabold text-gray-900">Track Your Order</h1>
          <p className="text-gray-500 mt-2">Enter your order number or use your tracking link</p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Order number (e.g. 1042)"
              className="w-full pl-10 pr-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl text-sm transition disabled:opacity-60 flex items-center gap-2"
          >
            {loading ? <RefreshCw size={15} className="animate-spin" /> : <Search size={15} />}
            Track
          </button>
        </form>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm flex items-center gap-2">
            <XCircle size={16} className="flex-shrink-0" /> {error}
          </div>
        )}

        {/* Order card */}
        {order && (
          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">

            {/* Card header */}
            <div className="bg-gray-50 border-b px-6 py-4 flex flex-wrap gap-4 items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold tracking-wide">Order</p>
                <p className="text-xl font-extrabold text-gray-900">#{order.id}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">Placed on</p>
                <p className="text-sm font-semibold text-gray-800">
                  {order.date
                    ? new Date(order.date).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })
                    : "—"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-lg font-bold text-orange-600">${Number(order.amount || 0).toFixed(2)}</p>
              </div>
            </div>

            {/* Live badge + share */}
            <div className="px-6 pt-4 flex items-center justify-between gap-3 flex-wrap">
              {isLive ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                  Live — refreshes every 30s
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  <Receipt size={12} /> {statusLabel(order.status)}
                </span>
              )}
              <div className="flex items-center gap-2">
                {lastRefresh && (
                  <span className="text-[11px] text-gray-400">
                    Updated {lastRefresh.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
                <button
                  onClick={() => fetchOrder(order.id, false)}
                  disabled={loading}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition"
                  title="Refresh"
                >
                  <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                </button>
                <button
                  onClick={copyLink}
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
                >
                  {copied ? <><Check size={12} className="text-green-500" /> Copied</> : <><Copy size={12} /> Share link</>}
                </button>
              </div>
            </div>

            {/* Progress tracker */}
            {isCancelled ? (
              <div className="px-6 py-8 flex flex-col items-center gap-3 text-center">
                <XCircle size={40} className="text-red-400" />
                <p className="font-bold text-red-600 text-lg">Order Cancelled</p>
                <p className="text-sm text-gray-500">This order has been cancelled.</p>
              </div>
            ) : (
              <div className="px-6 py-6">
                <div className="relative flex items-start justify-between">
                  <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 z-0" />
                  <div
                    className="absolute top-5 left-0 h-0.5 bg-orange-400 z-0 transition-all duration-700"
                    style={{ width: currentStep >= 0 ? `${(currentStep / (STATUS_STEPS.length - 1)) * 100}%` : "0%" }}
                  />
                  {STATUS_STEPS.map((step, idx) => {
                    const done    = idx <= currentStep;
                    const active  = idx === currentStep;
                    const Icon    = step.icon;
                    return (
                      <div key={step.key} className="relative z-10 flex flex-col items-center gap-2 flex-1">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                          active ? `${step.bg} ${step.color} ring-4 ring-orange-100`
                          : done  ? `${step.bg} ${step.color}`
                          : "bg-gray-100 text-gray-400"
                        }`}>
                          <Icon size={18} />
                        </div>
                        <p className={`text-[10px] font-semibold text-center leading-tight ${done ? "text-gray-800" : "text-gray-400"}`}>
                          {step.label}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <p className="text-center mt-6 text-sm font-semibold text-orange-600">
                  Current status: <span className="font-extrabold">{statusLabel(order.status)}</span>
                </p>
              </div>
            )}

            {/* Delivery address */}
            {order.delivery_address && (
              <div className="border-t px-6 py-4 flex items-start gap-2">
                <MapPin size={15} className="text-orange-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-0.5">Delivery address</p>
                  <p className="text-sm text-gray-800">{order.delivery_address}</p>
                </div>
              </div>
            )}

            {/* Items */}
            {order.items?.length > 0 && (
              <div className="border-t px-6 py-4">
                <p className="text-sm font-semibold text-gray-700 mb-3">Items</p>
                <div className="space-y-2">
                  {order.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 bg-orange-100 text-orange-700 rounded text-xs font-bold flex items-center justify-center">
                          {item.quantity}
                        </span>
                        <span className="text-gray-800">{item.item?.name || `Item #${item.menu_item_id}`}</span>
                      </div>
                      <span className="text-gray-600 font-medium">
                        ${(Number(item.price) * Number(item.quantity)).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {!order && !loading && !error && (
          <div className="text-center py-10 text-gray-400">
            <Package size={40} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm">Enter your order number above to track your delivery</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
